import { routes, type MyChatsResponse } from '@spot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useSession } from '../auth/session-gate';

export const MY_CHATS_KEY = ['me', 'chats'] as const;
/** S10 refreshes unread counts while it is on screen (paused when the app is hidden). */
const CHATS_POLL_MS = 15_000;

export function useMyChats() {
	const { api } = useSession();
	return useQuery<MyChatsResponse>({
		queryKey: MY_CHATS_KEY,
		queryFn: () => api.call(routes.myChats),
		refetchInterval: CHATS_POLL_MS,
		refetchIntervalInBackground: false,
	});
}

/** Accepted while the app is open: the tab bar and the event screen notice within a minute. */
const NEW_CHATS_POLL_MS = 60_000;

/** Ids of chats the viewer was accepted into and never opened (T-1335), slower than S10. */
export function useNewChatIds(): readonly string[] {
	const { api } = useSession();
	const { data } = useQuery<MyChatsResponse>({
		queryKey: MY_CHATS_KEY,
		queryFn: () => api.call(routes.myChats),
		refetchInterval: NEW_CHATS_POLL_MS,
		refetchIntervalInBackground: false,
	});
	return useMemo(
		() => data?.items.filter((chat) => chat.isNew).map((chat) => chat.eventId) ?? [],
		[data],
	);
}

/** Moves the read marker; the chats list then shows fewer unread. */
export function useMarkChatRead(eventId: string) {
	const { api } = useSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (seq: number) =>
			api.call(routes.markChatRead, { params: { id: eventId }, body: { seq } }),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: MY_CHATS_KEY });
		},
	});
}
